import React, { useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { CustomButton, Loading } from '../_common';
import { ASSIGN_DRIVER_TO_LOAD } from '../../graphql/client/mutations';
import './DriverItem.scss';
import * as Actions from '../../store/actions/addLoadModal';
import { AssignDriverToLoad } from '../../types/schemaTypes';
import { useStoreContext } from '../App/StoreProvider';
import { Row } from 'react-bootstrap';
import { Col } from 'react-bootstrap';
import { GET_ALL_DRIVERS_BY_REGION } from '../../graphql/client/queries';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface DriverItemProps {
    disabled: boolean;
    setDisabled: (isDisabled: boolean) => void;
    onAssignClick: (isSplit?: boolean) => void;
    driverName: string;
    driverId: number;
    driverCompany: string | null;
    isSplitLoad: boolean;
    loadId: number;
    primaryDriverName?: string;
    regionId: number;
    refetch?: () => void;
    isFromAddLoads?: boolean;
    trainingStatus: number | null;
    checkInWithBellyDump?: boolean | null;
}
const CONCURRENT_EDITS_ERROR_MESSAGE = 'Load no longer eligible for driver assignment, click OK to refresh.';
const DriverItem = ({
    disabled,
    setDisabled,
    driverName,
    driverId,
    driverCompany,
    isSplitLoad,
    onAssignClick,
    primaryDriverName,
    regionId,
    refetch,
    isFromAddLoads,
    loadId,
    trainingStatus,
    checkInWithBellyDump,
}: DriverItemProps): React.ReactElement => {
    const [state, dispatch] = useStoreContext();
    const {
        addLoadModal: { currentLoadId },
    } = state;
    const isPrimaryDriver = !isSplitLoad || false;
    const [errorMessage, setErrorMessage] = useState('');

    const onAssignDriver = (data: AssignDriverToLoad): void => {
        if (data.assignDriverToLoad === null) return;
        const assignDriverErrors = data.assignDriverToLoad.errors;
        if (assignDriverErrors && assignDriverErrors.length > 0) {
            setErrorMessage(assignDriverErrors[0] || '');
        } else {
            if (refetch) {
                refetch();
            }
            dispatch(
                Actions.assignDriverToLoadId(
                    currentLoadId,
                    driverName,
                    driverId,
                    isPrimaryDriver,
                    primaryDriverName || '',
                ),
            );
            onAssignClick(isSplitLoad);
        }
    };

    const [assignDriver, { loading: mutationLoading }] = useMutation<AssignDriverToLoad>(ASSIGN_DRIVER_TO_LOAD, {
        onCompleted: onAssignDriver,
        refetchQueries: [
            {
                query: GET_ALL_DRIVERS_BY_REGION,
                variables: {
                    regionId: regionId,
                },
            },
        ],
    });

    const onClick = async (): Promise<void> => {
        setDisabled(true);
        if (isFromAddLoads) {
            await dispatch(Actions.removeDriverFromLoad(loadId));
            await dispatch(
                Actions.assignDriverToLoadId(
                    currentLoadId,
                    driverName,
                    driverId,
                    isPrimaryDriver,
                    primaryDriverName || '',
                ),
            );
            await dispatch(Actions.addDriverIdToDriverList(driverId));
            onAssignClick(isSplitLoad);
            setDisabled(false);
        } else {
            try {
                await assignDriver({
                    variables: { driverId: driverId, loadId: currentLoadId, isPrimaryDriver: isPrimaryDriver },
                });
            } finally {
                setDisabled(false);
            }
        }
    };

    const onClickOK = (): void => {
        window.location.reload();
    };

    return (
        <Row className="DriverItem-Row">
            {mutationLoading ? (
                <div className="DriverItem-Container">
                    <div className="DriverItem-Spinner">
                        <Loading />
                    </div>
                </div>
            ) : (
                <div className="DriverItem-Container">
                    <Col className="DriverItem-NameCol">
                        <div className="DriverItem-Name-Company-Container">
                            <div className="DriverItem-Name">
                                {/* {trainingStatus === 2 ? (
                                    <div className="expired"></div>
                                ) : trainingStatus === 3 ? (
                                    <div className="pass"></div>
                                ) : null} */}
                                {driverName}
                                {/* Drivers in this list are server-filtered to be on-shift + available. */}
                                <span
                                    className="conv-icon on-shift DriverItem-BellyDumpIcon"
                                    title={
                                        checkInWithBellyDump
                                            ? 'Checked in with belly dump (hopper)'
                                            : 'Checked in with box'
                                    }
                                >
                                    <FontAwesomeIcon
                                        icon={['fas', checkInWithBellyDump ? 'dumpster' : 'cube']}
                                        color="#b6bd00"
                                    />
                                </span>
                            </div>
                            <div className="DriverItem-Company">{driverCompany}</div>
                        </div>
                    </Col>
                    {errorMessage !== '' && <div className="DriverItem-Assign-Error">{errorMessage}</div>}
                    {errorMessage === CONCURRENT_EDITS_ERROR_MESSAGE ? (
                        <Col className="DriverItem-ButtonCol">
                            <div className="DriverItem-ButtonContainer">
                                <CustomButton title="OK" isBordered onClick={onClickOK}></CustomButton>
                            </div>
                        </Col>
                    ) : (
                        <Col className="DriverItem-ButtonCol">
                            <div className="DriverItem-ButtonContainer">
                                <CustomButton
                                    title="Assign Driver"
                                    isBordered
                                    onClick={onClick}
                                    isDisabled={disabled}
                                />
                            </div>
                        </Col>
                    )}
                </div>
            )}
        </Row>
    );
};

export default DriverItem;

